import {graphql} from 'react-relay';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useForecastFetchQuery} from '../../../../forecast-app/shared/hooks/useForecastFetchQuery';
import Util from '../../../../forecast-app/shared/util/util';
import {ValueCandidate, ValueSource} from './valueSource.types';

function filterBySearch(items: ValueCandidate[], search: string): ValueCandidate[] {
	const q = search.trim().toLowerCase();
	return q ? items.filter(i => i.label.toLowerCase().includes(q)) : items;
}

/** Candidate order comes from whatever the backing endpoint happened to return, and not every one
 * of them orders its rows: `LabelLogic.getLabelsByType` (what `labels(labelType: TASK)` reaches)
 * has no ORDER BY at all, unlike the unfiltered `getLabelsByCompanyId` it replaced, which sorted by
 * name. Sort here so every picker in this family reads alphabetically regardless. */
function byLabel(items: ValueCandidate[]): ValueCandidate[] {
	return [...items].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Builds a ValueSource backed by a single Relay query that returns a flat list of
 * {id, label} candidates. The query is fetched once on mount. resolveLabels reads
 * from a module-level cache populated by the last successful fetch, so a candidate's
 * label is available synchronously even for a component that never called useCandidates
 * (e.g. a summary line rendered elsewhere in the tree).
 */
export function createRelayListValueSource<T>(
	query: Parameters<typeof useForecastFetchQuery>[0],
	selectRows: (data: T | null) => ValueCandidate[]
): ValueSource {
	let labelCache: Record<string, string> = {};

	return {
		useCandidates(search: string) {
			const {data, fetch, loading} = useForecastFetchQuery<T, Record<string, unknown>>(query);
			const [error, setError] = useState(false);
			const runFetch = useCallback(() => {
				setError(false);
				fetch(undefined, undefined, () => setError(true));
				// empty deps intentionally keeps this stable across renders; fetch's identity is
				// re-created every render by useForecastFetchQuery, but it always closes over the
				// same query/environment, so calling the version captured here is equivalent
			}, []);
			useEffect(() => {
				runFetch();
				// empty deps intentionally runs the fetch once on mount; runFetch/selectRows are
				// omitted by design
			}, []);
			// Memoized on the payload: `useCandidates` re-renders on every keystroke in the picker's
			// search field, and re-selecting plus re-sorting a 10k-row list on each one is wasted work
			// - the rows only change when a fetch resolves.
			const items = useMemo(() => byLabel(selectRows(data)), [data]);
			useEffect(() => {
				if (items.length > 0) {
					labelCache = {...labelCache, ...Object.fromEntries(items.map(i => [i.id, i.label]))};
				}
			}, [items]);
			return {items: filterBySearch(items, search), loading, error, retry: runFetch};
		},
		resolveLabels(ids: string[]) {
			const out: Record<string, string> = {};
			ids.forEach(id => {
				if (labelCache[id] !== undefined) {
					out[id] = labelCache[id];
				}
			});
			return out;
		},
	};
}

// Verified against schema.graphql: Viewer.projects -> ProjectTypeConnection, ProjectType.{id, name}.
// Company.allProjects is NOT usable here: its resolver returns null unless a searchQuery with
// filters is supplied, so the builder's plain `first:` query came back empty with no error and
// the dropdown read "No matches" on an account with projects.
//
// excludeRestricted, like every other project filter dropdown in the app (FilterQueryContent,
// ProjectListPage, TimesheetsPage, MyNotifications): ProjectLogic.getAllProjects defaults it to
// false, and `if (onlyRestricted || !excludeRestricted)` then UNIONS in the projects the viewer has
// no access to. Offering one is a pick that can never match a row the report is allowed to show,
// and it puts a restricted project's name in front of someone who cannot open it.
const projectListQuery = graphql`
	query relayValueSourcesProjectQuery {
		viewer {
			projects(first: 10000, excludeRestricted: true) {
				edges {
					node {
						id
						name
					}
				}
			}
		}
	}
`;

interface ProjectListQueryData {
	viewer?: {
		projects?: {
			edges?: Array<{node: {id: string; name: string | null}}>;
		};
	};
}

/** A Relay global id is base64 of "Type:id"; the filter wire value is the raw integer id, which is
 * what the query engine compares and what the authoring agent already writes. Decoding here is what
 * lets a stored value resolve to its name instead of collapsing to "N selected".
 *
 * Returns nothing for a node that cannot produce a usable candidate: no name to show, or an id that
 * is not a relay global id. `Util.getIdFromBase64String` is `parseInt(atob(str).split(':')[1])`, so
 * it yields NaN for a decoded string with no numeric second half and throws for a string that is not
 * base64 at all. Either way the only candidate it could build is one whose id no row can match and
 * whose value the query engine cannot parse, so the option is dropped rather than offered. */
function toCandidate(id: string, name: string | null): ValueCandidate | null {
	if (!name) {
		return null;
	}
	let decoded: number;
	try {
		decoded = Util.getIdFromBase64String(id);
	} catch {
		return null;
	}
	return Number.isFinite(decoded) ? {id: String(decoded), label: name} : null;
}

/** The five selectors below differ only in where the edges live and which field carries the name,
 * so the map/filter/never-null plumbing is written once here. */
function toCandidates<N extends {id: string}>(
	edges: Array<{node: N}> | undefined,
	nameOf: (node: N) => string | null
): ValueCandidate[] {
	return (edges ?? []).map(e => toCandidate(e.node.id, nameOf(e.node))).filter((c): c is ValueCandidate => c !== null);
}

export function selectProjectRows(data: ProjectListQueryData | null): ValueCandidate[] {
	return toCandidates(data?.viewer?.projects?.edges, node => node.name);
}

export const projectValueSource: ValueSource = createRelayListValueSource<ProjectListQueryData>(
	projectListQuery,
	selectProjectRows
);

// Verified against schema.graphql: Company.allPersons -> PersonTypeConnection, Person.{id, fullName}.
const personListQuery = graphql`
	query relayValueSourcesPersonQuery {
		viewer {
			company {
				allPersons(first: 10000) {
					edges {
						node {
							id
							fullName
						}
					}
				}
			}
		}
	}
`;

interface PersonListQueryData {
	viewer?: {
		company?: {
			allPersons?: {
				edges?: Array<{node: {id: string; fullName: string | null}}>;
			};
		};
	};
}

export function selectPersonRows(data: PersonListQueryData | null): ValueCandidate[] {
	return toCandidates(data?.viewer?.company?.allPersons?.edges, node => node.fullName);
}

export const personValueSource: ValueSource = createRelayListValueSource<PersonListQueryData>(
	personListQuery,
	selectPersonRows
);

// Verified against schema.graphql: Company.clients -> ClientTypeConnection, Client.{id, name}.
const clientListQuery = graphql`
	query relayValueSourcesClientQuery {
		viewer {
			company {
				clients(first: 10000) {
					edges {
						node {
							id
							name
						}
					}
				}
			}
		}
	}
`;

interface ClientListQueryData {
	viewer?: {
		company?: {
			clients?: {
				edges?: Array<{node: {id: string; name: string}}>;
			};
		};
	};
}

export function selectClientRows(data: ClientListQueryData | null): ValueCandidate[] {
	return toCandidates(data?.viewer?.company?.clients?.edges, node => node.name);
}

export const clientValueSource: ValueSource = createRelayListValueSource<ClientListQueryData>(
	clientListQuery,
	selectClientRows
);

// labelType: TASK because a task report can only ever match a task label. Without it the dropdown
// offered every label on the account, including project-only and people-only ones, which are dead
// options that can never match a row.
// Verified against schema.graphql: Company.labels -> LabelTypeConnection, Label.{id, name}, and
// LABEL_TYPE enum includes TASK.
const labelListQuery = graphql`
	query relayValueSourcesLabelQuery {
		viewer {
			company {
				labels(first: 10000, labelType: TASK) {
					edges {
						node {
							id
							name
						}
					}
				}
			}
		}
	}
`;

interface LabelListQueryData {
	viewer?: {
		company?: {
			labels?: {
				edges?: Array<{node: {id: string; name: string | null}}>;
			};
		};
	};
}

export function selectLabelRows(data: LabelListQueryData | null): ValueCandidate[] {
	return toCandidates(data?.viewer?.company?.labels?.edges, node => node.name);
}

export const labelValueSource: ValueSource = createRelayListValueSource<LabelListQueryData>(labelListQuery, selectLabelRows);

// Verified against schema.graphql: Company.roles -> RoleTypeConnection, Role.{id, name}.
// includeDisabled is false so a retired role is not offered as a filter value.
const roleListQuery = graphql`
	query relayValueSourcesRoleQuery {
		viewer {
			company {
				roles(first: 10000, includeDisabled: false) {
					edges {
						node {
							id
							name
						}
					}
				}
			}
		}
	}
`;

interface RoleListQueryData {
	viewer?: {
		company?: {
			roles?: {
				edges?: Array<{node: {id: string; name: string | null}}>;
			};
		};
	};
}

export function selectRoleRows(data: RoleListQueryData | null): ValueCandidate[] {
	return toCandidates(data?.viewer?.company?.roles?.edges, node => node.name);
}

export const roleValueSource: ValueSource = createRelayListValueSource<RoleListQueryData>(roleListQuery, selectRoleRows);

// Workflow status candidates.
//
// schema.graphql exposes per-project custom status columns only
// (ProjectType.statusColumnsV2 / ProjectGroupType.statusColumns) — there is no
// company-wide connection enumerating status column names across all projects, and
// the ValueSource interface has no project-scoping parameter to fetch one project's
// columns. The only status classification that is global and requires no project
// context is the fixed STATUS_CATEGORY enum (TODO / INPROGRESS / DONE), which is the
// same category set the legacy "Task Status" filter dropdown lists via
// getWorkFlowCategoryFilters — that legacy dropdown never issues a Relay query either;
// it is a static local list. The candidate ids (TODO/INPROGRESS/DONE) are built with
// translated labels in `useValueSource`, which is why there is no exported
// `workflowStatusValueSource` constant here anymore.
